import { Card, Button, Form, Dropdown } from "react-bootstrap";
import { useState, useMemo } from "react";

// Helper
const timeToSeconds = (timeString) => {
  if (!timeString || typeof timeString !== "string") return 0;

  const parts = timeString.split(":").map(Number);
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  const s = parts[2] || 0;

  return h * 3600 + m * 60 + s;
};

const calculateProductiveTime = (worked, breakTime, idleTime, meetingTime) => {
  const productiveSeconds = Math.max(
    0,
    timeToSeconds(worked) -
      timeToSeconds(breakTime) -
      timeToSeconds(idleTime) -
      timeToSeconds(meetingTime)
  );
  const hours = Math.floor(productiveSeconds / 3600);
  const minutes = Math.floor((productiveSeconds % 3600) / 60);
  const seconds = productiveSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const PerformanceOfAgents = ({ reports = [], onRowClick }) => {
  const [search, setSearch] = useState("");
  const [selectedName, setSelectedName] = useState("");

  // Get unique names
  const allNames = useMemo(() => {
    const names = reports.map((r) => r.name);
    return [...new Set(names)];
  }, [reports]);

  // Filter names inside dropdown
  const filteredNames = allNames.filter((name) =>
    name.toLowerCase().includes(search.toLowerCase())
  );

  // Filter table data
  const filteredReports = reports.filter((r) =>
    selectedName
      ? r.name === selectedName
      : true
  );

  return (
    <Card className="bg-white border-0 rounded-3 mb-4">
      <Card.Body className="p-0">
        <div className="p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h3 className="mb-0">Today's Performance Report</h3>

          {/* 🔎 Search Dropdown */}
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-secondary">
              {selectedName || "Search by Name"}
            </Dropdown.Toggle>

            <Dropdown.Menu style={{ minWidth: "250px", padding: "10px" }}>
              <Form.Control
                type="text"
                placeholder="Search name..."
                className="mb-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedName("");
                    setSearch("");
                  }}
                >
                  All Employees
                </Dropdown.Item>

                {filteredNames.map((name, index) => (
                  <Dropdown.Item
                    key={index}
                    onClick={() => {
                      setSelectedName(name);
                      setSearch("");
                    }}
                  >
                    {name}
                  </Dropdown.Item>
                ))}

                {filteredNames.length === 0 && (
                  <div className="text-center text-muted small">
                    No names found
                  </div>
                )}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="default-table-area style-two all-projects">
          <div className="table-responsive">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Worked Time</th>
                  <th>Break Time</th>
                  <th>Idle Time</th>
                  <th>Meeting Time</th>
                  <th>Productive Time</th>
                  <th>Productivity</th>
                  <th>View Report</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((agent) => (
                  <tr
                    key={agent.user_id + agent.date}
                    onClick={() => onRowClick?.(agent)}
                    style={{
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                  >
                    <td>{agent.name}</td>
                    <td>{agent.workedHours}</td>
                    <td>{agent.breakTime}</td>
                    <td>{agent.idleTime}</td>
                    <td>{agent.meetingTime}</td>
                    <td>
                      {calculateProductiveTime(
                        agent.workedHours,
                        agent.breakTime,
                        agent.idleTime,
                        agent.meetingTime
                      )}
                    </td>
                    <td>
                      <div className="progress-cell">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${Math.min(
                                agent.productivity,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                        <span className="progress-value">
                          {agent.productivity}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick?.(agent);
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}

                {filteredReports.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default PerformanceOfAgents;